import ChallengeTypeTag from 'core/chatbot/components/atoms/ChallengeTypeTag/ChallengeTypeTag';
import { Icon } from 'ui';
import TextHtml from 'core/modules/atoms/TextHtml';
import TextRich from 'core/modules/atoms/TextRich/TextRich';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import languageColor from '../../../assets/images/language_color.png';
import mathsColor from '../../../assets/images/maths_color.png';
import FilledCircularProgress from './FilledCircularProgress';

// Function to map subject names to icon images
const getSubjectIcon = (subject) => {
  if (!subject) return mathsColor; // Default fallback

  const subjectLower = subject.toLowerCase();

  // Map subject names to icon images - only mathematics and language
  const subjectMap = {
    mathematics: mathsColor,
    language: languageColor,
  };

  // Find matching subject
  for (const [key, iconImage] of Object.entries(subjectMap)) {
    if (subjectLower.includes(key)) {
      return iconImage;
    }
  }

  return mathsColor; // Default fallback
};

const RecommendationCard = ({ recommendation, onClick, containerRef }) => {
  const { t } = useTranslation(['chatbot']);
  const cardRef = useRef(null);

  const handleClick = () => {
    onClick(recommendation);
  };

  const handleMouseEnter = () => {
    if (cardRef.current && containerRef?.current) {
      // Delay the scroll calculation to allow CSS animation to complete
      setTimeout(() => {
        const card = cardRef.current;
        const container = containerRef.current;

        if (!card || !container) return;

        // Get current positions
        const cardRect = card.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Calculate relative positions
        const cardLeft = cardRect.left - containerRect.left;
        const cardRight = cardRect.right - containerRect.left;
        const containerWidth = containerRect.width;

        // Check if card is cropped on the left
        if (cardLeft < 0) {
          container.scrollTo({
            left: container.scrollLeft + cardLeft - 20, // 20px padding
            behavior: 'smooth',
          });
          return;
        }

        // Check if card is currently cropped on the right
        if (cardRight > containerWidth) {
          const scrollAmount = cardRight - containerWidth + 20;
          container.scrollTo({
            left: container.scrollLeft + scrollAmount,
            behavior: 'smooth',
          });
        }
      }, 100); // 100ms matches the CSS animation duration
    }
  };

  // Calculate completion percentage
  const completionPercentage =
    recommendation.totalActivities > 0 ? Math.round(((recommendation.activitiesCompleted || 0) / recommendation.totalActivities) * 100) : 0;

  // Determine the practice session status
  const getSessionStatus = () => {
    if (recommendation.completed || (recommendation.activitiesCompleted === recommendation.totalActivities && recommendation.totalActivities > 0)) {
      return t('practice_session_completed');
    }
    if (recommendation.activitiesCompleted > 0) {
      return t('practice_session_in_progress');
    }
    return t('practice_session_not_started');
  };

  return (
    <div ref={cardRef} className="student-recommendations__card" onClick={handleClick} onMouseEnter={handleMouseEnter} role="button" tabIndex={0}>
      <div className="student-recommendations__card-body">
        <div className="student-recommendations__card-header">
          <div className="student-recommendations__card-header--left">
            <div className="student-recommendations__card-icon">
              <img src={getSubjectIcon(recommendation.sessionData?.subject)} alt={recommendation.sessionData?.subject || t('Subject')} />
            </div>
            <div className="student-recommendations__card-content">
              <h3 className="student-recommendations__card-title">
                <TextHtml text={recommendation.title} />
              </h3>
              <p className="student-recommendations__card-subtitle">
                <TextRich text={recommendation.subtitle} />
              </p>
            </div>
          </div>
          <div className="student-recommendations__card-header--right">
            <div className="student-recommendations__card-progress">
              <FilledCircularProgress size={24} percentage={completionPercentage} backgroundColor="#E0E0E0" progressColor="#41D158">
                <div className="student-recommendations__card-check-icon">
                  <Icon name="check" />
                </div>
              </FilledCircularProgress>
            </div>
          </div>
        </div>
      </div>
      <div className="student-recommendations__card-bottom">
        <div className="student-recommendations__card-progress-bar">
          <div className="student-recommendations__card-progress-bar-fill" style={{ width: `${completionPercentage}%` }} />
        </div>

        <div className="student-recommendations__card-footer">
          <ChallengeTypeTag challengeType={recommendation.challengeType} completed={recommendation.completed} size="small" />
          <span className="student-recommendations__session-status">{getSessionStatus()}</span>
        </div>
      </div>
    </div>
  );
};

RecommendationCard.propTypes = {
  recommendation: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    icon: PropTypes.string,
    challengeType: PropTypes.oneOf(['new', 'review', 'Refuerzo microtest', 'Práctica mezclada', 'Del Docente']),
    totalActivities: PropTypes.number.isRequired,
    activitiesCompleted: PropTypes.number,
    completed: PropTypes.bool,
    sessionData: PropTypes.object, // Contains the original session data including activityInstances
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  containerRef: PropTypes.shape({ current: PropTypes.any }),
};

export default RecommendationCard;
